import { useAuthStore, useAuthTokenStore } from '../../store/authStore';
import { login, logout, refresh, activate, resendVerification, findPassword, resetPassword, checkEmail, checkHandle, deferredSignup, signUp } from '../../api/authAPI';
import { UserData, UserDataWithRefreshToken } from '../../dto/EntityRepository';
import { ActivateReq, FindPasswordReq, LoginReq, ResendVerificationReq, SignUpReq } from '../../dto/ReqDtoRepository';
import { AuthResponse, SimpleStringResponse } from '../../dto/ResDtoRepository';
import { useStandardAlertStore } from '../../store/portal/alertStore';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { getTempAlertMessage } from '../../constants/tempAlertMessages';
import { AlertPosition, AlertType, ModalType, TempAlertPosition, TempAlertType } from '../../enum/EnumRepository';
import { useApiWrapper } from './apiWrapper';
import { useModalStore } from '../../store/portal/modalStore';
import { useOnBoardStore } from '../../store/onBoardStore';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';


interface UseAuthResult {
  user: UserData | null;
  setUser: (user: UserData) => void;
  isOwner: boolean;
  deferredSignup: (data: SignUpReq) => Promise<void>;
  signUp: (data: SignUpReq) => Promise<void>;
  login: (data: LoginReq) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  activate: (data: ActivateReq) => Promise<void>;
  resend: (data: ResendVerificationReq) => Promise<void>;
  findPassword: (data: FindPasswordReq) => Promise<void>;
  resetPassword: (data: ActivateReq) => Promise<void>;
  checkEmail: (email: string) => Promise<void>;
  checkHandle: (handle: string) => Promise<void>;
}

export const useAuth = (): UseAuthResult => {
  const { user, setUser, clearUser } = useAuthStore();
  const { authToken, setAuthToken, clearAuthToken } = useAuthTokenStore();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const isCurrentPageOwner =
    !!authToken && !!pageOwner && user?.aui === pageOwner.aui;
  const { setTempAlert } = useTempAlertStore();
  const { setStandardModal, clearModal } = useModalStore();
  const withApiHandler = useApiWrapper();
  const { enqueueAlert } = useStandardAlertStore();
  const { updateOnBoardingData } = useOnBoardStore();

  const settingLoginUser = (data: UserDataWithRefreshToken) => {
    clearModal();
    const onlyUserData: UserData = {
      id: data.id,
      email: data.email,
      username: data.username,
      aui: data.aui,
      handle: data.handle,
      role: data.role,
      subscriptionStatus: data.subscriptionStatus,
      passwordStatus: data.passwordStatus,
      // landingGuide: data.landingGuide
    };
    setUser(onlyUserData);
    setAuthToken(data.accessToken);
    setTempAlert({
      type: TempAlertType.UPDATED,
      position: TempAlertPosition.RB,
      content: "Welcome .",
      duration: 2000
    });
  }

  const handleLoginSuccess = (response: AuthResponse) => {
    const { data } = response;
    settingLoginUser(data);
  };

  const handleRefreshSuccess = (response: SimpleStringResponse) => {
    const { data } = response;
    if (data.value) {
      setAuthToken(data.value);
    };
  };

  const handleActivateSuccess = (response: AuthResponse) => {
    const { data } = response;
    settingLoginUser(data);
  };

  const handleResendVerificationSuccess = (response: SimpleStringResponse) => {
    const { data } = response;
    setTempAlert({
      type: TempAlertType.UPDATED,
      position: TempAlertPosition.RB,
      content: getTempAlertMessage('VERIFICATION_CODE_SENT'),
      duration: 2000
    });
  };

  const handleFindPasswordSuccess = (response: SimpleStringResponse) => {
    const { data } = response;
    setTempAlert({
      type: TempAlertType.UPDATED,
      position: TempAlertPosition.RB,
      content: getTempAlertMessage('VERIFY_EMAIL_PASSWORD_RESET'),
      duration: 2000
    });
    setStandardModal({
      modalType: ModalType.VERIFICATION,
      title: "find_password",
      value: data.value || null,
      handleChange: () => { }
    });
  };

  const handleResetPasswordSuccess = (response: SimpleStringResponse) => {
    const { data } = response;
    setTempAlert({
      type: TempAlertType.UPDATED,
      position: TempAlertPosition.RB,
      content: getTempAlertMessage('SENT_TO_EMAIL_CHECK_INBOX'),
      duration: 2000
    });
    clearModal();
  };

  const handleLogoutSuccess = (response: SimpleStringResponse) => {
    const { data } = response;
    clearUser();
    clearAuthToken();
    setTempAlert({
      type: TempAlertType.UPDATED,
      position: TempAlertPosition.RB,
      content: "Goodbye.",
      duration: 3000
    });
  };

  const handleCheckEmailSuccess = (response: SimpleStringResponse) => {
    const { data } = response;
    const verifiedEmail = data.value === "True" ? true : false;
    updateOnBoardingData({ 
      verifiedEmail: verifiedEmail
    });
    if (!verifiedEmail) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: data.message,
      });
    }
  };

  const handleCheckHandleSuccess = (response: SimpleStringResponse) => {
    const { data } = response;
    const verifiedHandle = data.value === "True" ? true : false;
    updateOnBoardingData({ 
      verifiedHandle: verifiedHandle
    });
    if (!verifiedHandle) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: data.message,
      });
    }
  };

  const handleSignupSuccess = (response: SimpleStringResponse) => {
    const { data } = response;
    setTempAlert({
      type: TempAlertType.UPDATED,
      position: TempAlertPosition.RB,
      content: getTempAlertMessage('VERIFY_YOUR_EMAIL'),
      duration: 2000
    });
    setStandardModal({
      modalType: ModalType.DEFERRED_EMAIL_VERIFICATION,
      title: "signup",
      value: data.value || null,
      handleChange: () => { }
    });
  };

  const handleAuthRequest = async <T extends SignUpReq | LoginReq | ActivateReq | ResendVerificationReq | FindPasswordReq | string | null>(
    action: 'login' | 'refresh' | 'activate' | 'logout' | 'resend' | 'find_password' | 'reset_password' | 'check_email' | 'check_handle' | 'deferred_signup' | 'signup',
    data: T
  ) => {
    const apiFunction = async () => {
      switch (action) {
        case 'check_email':
          return handleCheckEmailSuccess(await checkEmail(data as string));
        case 'check_handle':
          return handleCheckHandleSuccess(await checkHandle(data as string));
        case 'deferred_signup':
          return handleSignupSuccess(await deferredSignup(data as SignUpReq));
        case 'signup':
          return handleSignupSuccess(await signUp(data as SignUpReq));
        case 'refresh':
          return handleRefreshSuccess(await refresh());
        case 'activate':
          return handleActivateSuccess(await activate(data as ActivateReq));
        case 'resend':
          return handleResendVerificationSuccess(await resendVerification(data as ResendVerificationReq));
        case 'find_password':
          return handleFindPasswordSuccess(await findPassword(data as FindPasswordReq));
        case 'reset_password':
          return handleResetPasswordSuccess(await resetPassword(data as ActivateReq));
        case 'logout':
          return handleLogoutSuccess(await logout());
        case 'login':
        default:
          return handleLoginSuccess(await login(data as LoginReq));
      }
    };

    await withApiHandler(apiFunction, [action, data]);
  };

  const deferredSignupHandler = (data: SignUpReq) => handleAuthRequest('deferred_signup', data);
  const signUpHandler = (data: SignUpReq) => handleAuthRequest('signup', data);
  const loginHandler = (data: LoginReq) => handleAuthRequest('login', data);
  const refreshHandler = () => handleAuthRequest('refresh', null);
  const activateHandler = (data: ActivateReq) => handleAuthRequest('activate', data);
  const resendHandler = (data: ResendVerificationReq) => handleAuthRequest('resend', data);
  const findPasswordHandler = (data: FindPasswordReq) => handleAuthRequest('find_password', data);
  const resetPasswordHandler = (data: ActivateReq) => handleAuthRequest('reset_password', data);
  const logoutHandler = () => handleAuthRequest('logout', null);
  const checkEmailHandler = (data: string) => handleAuthRequest('check_email', data as string);
  const checkHandleHandler = (data: string) => handleAuthRequest('check_handle', data as string);

  return {
    user,
    setUser,
    isOwner: isCurrentPageOwner,
    login: loginHandler,
    logout: logoutHandler,
    refresh: refreshHandler,
    activate: activateHandler,
    resend: resendHandler,
    findPassword: findPasswordHandler,
    resetPassword: resetPasswordHandler,
    checkEmail: checkEmailHandler,
    checkHandle: checkHandleHandler,
    deferredSignup: deferredSignupHandler,
    signUp: signUpHandler,
  };
}
